import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { AdminStatsResponse } from '@app/shared';
import { PageHeader } from '../../components/PageHeader';
import { useT } from '../../i18n';
import { api } from '../../lib/api';
import { sourceGroupName } from './source-label';

function formatUsd(amount: number | null): string {
  if (amount === null) return '—';
  // Sub-cent costs would round to $0.00 with two decimals.
  const decimals = amount > 0 && amount < 1 ? 4 : 2;
  return `$${amount.toFixed(decimals)}`;
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="p-3 rounded-xl bg-surface">
      <div className="text-xs uppercase opacity-60 mb-2">{title}</div>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="opacity-70">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

export function AdminDashboard() {
  const t = useT();
  const { data, isLoading, error, refetch } = useQuery<AdminStatsResponse>({
    queryKey: ['admin', 'stats'],
    queryFn: () => api.adminGetStats(),
    staleTime: 60_000,
  });

  const links: Array<{ to: string; label: string }> = [
    { to: '/admin/participants', label: t.admin.participants },
    { to: '/admin/payments', label: t.adminPay.payments },
    { to: '/admin/packs', label: t.adminPay.packs },
    { to: '/admin/invites', label: t.admin.invites },
    { to: '/admin/waitlist', label: t.admin.waitlist },
    { to: '/admin/settings', label: t.admin.settings },
    { to: '/admin/broadcast', label: t.admin.broadcast },
    { to: '/admin/prompts', label: t.admin.prompts },
    { to: '/admin/costs', label: t.admin.costs },
    { to: '/admin/eval', label: t.admin.evalSet },
  ];

  return (
    <div className="min-h-screen p-4 max-w-lg mx-auto pb-12 safe-top safe-bottom">
      <PageHeader title={t.admin.title} to="/settings" />

      <div className="space-y-2 mb-6">
        {links.map((l) => (
          <Link key={l.to} to={l.to} className="block p-3 rounded-xl bg-surface text-sm">
            {l.label}
          </Link>
        ))}
      </div>

      {isLoading && <div className="mb-6 text-sm opacity-60">{t.admin.statsLoading}</div>}

      {error && (
        <div className="mb-6 p-3 rounded-xl bg-surface text-sm">
          <div className="mb-2">{t.admin.statsFailed}</div>
          <button type="button" className="underline" onClick={() => refetch()}>
            {t.common.retry}
          </button>
        </div>
      )}

      {data && (
        <div className="space-y-2">
          <Block title={t.admin.users}>
            <Row label={t.admin.usersTotal} value={String(data.users.total)} />
            <Row label={t.admin.usersWithAccess} value={String(data.users.with_access)} />
            <Row label={t.admin.usersWaiting} value={String(data.users.waiting)} />
            <Row label={t.admin.usersNew7d} value={String(data.users.new_7d)} />
            <Row label={t.admin.usersNew30d} value={String(data.users.new_30d)} />
          </Block>
          <Block title={t.admin.sourcesTitle}>
            {data.sources.groups.length === 0 ? (
              <div className="opacity-60">{t.admin.sourcesEmpty}</div>
            ) : (
              data.sources.groups.map((g) => (
                <Row
                  key={g.group}
                  label={sourceGroupName(g.group, t)}
                  value={t.admin.sourceCounts(g.came, g.first_result, g.paid)}
                />
              ))
            )}
            {data.sources.top_posts.length > 0 && (
              <>
                <div className="text-xs opacity-60 pt-2">{t.admin.sourcesTopPosts}</div>
                {data.sources.top_posts.map((p) => (
                  <Row key={p.label} label={p.label} value={String(p.came)} />
                ))}
              </>
            )}
          </Block>
          <Block title={t.admin.creditsBlock}>
            <Row label={t.admin.creditsGranted} value={String(data.credits.granted)} />
            <Row label={t.admin.creditsSpent} value={String(data.credits.spent)} />
            <Row label={t.admin.creditsLive} value={String(data.credits.live_balance)} />
          </Block>
          <Block title={t.admin.llm}>
            <Row label={t.admin.llmSpent} value={formatUsd(data.llm.total_spent_usd)} />
            <Row label={t.admin.llmBalance} value={formatUsd(data.llm.balance_usd)} />
            {data.llm.total_spent_usd === null && (
              <div className="text-xs opacity-60 pt-1">{t.admin.llmUnavailable}</div>
            )}
          </Block>
          <Block title={t.admin.ratings}>
            <div className="font-medium tabular-nums">
              {t.admin.ratingsLine(data.ratings.up, data.ratings.down, data.ratings.like_share)}
            </div>
            {data.ratings.last_comments.length > 0 && (
              <>
                <div className="text-xs opacity-60 pt-2">{t.admin.ratingsComments}</div>
                {data.ratings.last_comments.map((c) => (
                  <div key={c.job_id} className="break-words">
                    <span className="opacity-60">#{c.job_id}</span> {c.comment}
                  </div>
                ))}
              </>
            )}
          </Block>
        </div>
      )}
    </div>
  );
}
